import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMyTournaments, usePlayerTournaments, usePublicProfile } from '../api/hooks';
import { Loader, ErrorView, EmptyView } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { backButton } from '../telegram';
import { seasonLabel, shortDate } from '../format';

type Filter = 'all' | 'inmoney' | 'wins' | number; // number = seasonId

/** Tournament history — own (/me/tournaments) or another player's (/players/:id/tournaments). */
export function MyTournamentsScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const pid = id ? Number(id) : null;
  const mineQ = useMyTournaments(!pid);
  const playerQ = usePlayerTournaments(pid ?? NaN);
  const profileQ = usePublicProfile(pid ?? NaN);
  const { data, isLoading, isError, error } = pid ? playerQ : mineQ;
  const playerName = profileQ.data?.displayName;
  const title = pid ? (playerName ? `Турниры · ${playerName}` : 'Турниры игрока') : 'Мои турниры';
  // Фильтр живёт в URL (паттерн ScheduleScreen): подсветка таба всегда
  // соответствует реальному фильтру и переживает back-навигацию/remount.
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('filter');
  const filter: Filter =
    raw === 'wins' || raw === 'inmoney'
      ? raw
      : raw && /^\d+$/.test(raw)
        ? Number(raw)
        : 'all';
  const setFilter = (f: Filter) =>
    setSearchParams(f === 'all' ? {} : { filter: String(f) }, { replace: true });

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  const seasons = useMemo(() => {
    const s = new Set<number>();
    (data ?? []).forEach((t) => t.seasonId !== null && s.add(t.seasonId));
    return [...s].sort((a, b) => b - a);
  }, [data]);

  if (isLoading) return <Loader />;
  if (isError) return <ErrorView message={(error as Error)?.message} />;
  if (!data || data.length === 0) return <EmptyView message="Пока нет сыгранных турниров" />;

  const wins = data.filter((t) => t.position === 1).length;
  const inMoney = data.filter((t) => t.points > 0).length;
  const itmPct = data.length ? Math.round((inMoney / data.length) * 100) : 0;

  const list = data.filter((t) =>
    filter === 'all'
      ? true
      : filter === 'inmoney'
        ? t.points > 0
        : filter === 'wins'
          ? t.position === 1
          : t.seasonId === filter,
  );

  return (
    <div>
      <div className="subhead">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">{title}</div>
      </div>

      {/* Фильтры — сразу за «Все», иначе при длинном списке сезонов
          «В очках» и «Победы» уезжают за экран. */}
      <div className="season-switcher">
        <button
          className={`pill ${filter === 'all' ? 'pill--gold' : ''}`}
          onClick={() => setFilter('all')}
        >
          Все
        </button>
        <button
          className={`pill ${filter === 'inmoney' ? 'pill--gold' : ''}`}
          onClick={() => setFilter('inmoney')}
        >
          В очках
        </button>
        <button
          className={`pill ${filter === 'wins' ? 'pill--gold' : ''}`}
          onClick={() => setFilter('wins')}
        >
          Победы
        </button>
        {seasons.map((s) => (
          <button
            key={s}
            className={`pill ${filter === s ? 'pill--gold' : ''}`}
            onClick={() => setFilter(s)}
          >
            {seasonLabel(s)}
          </button>
        ))}
      </div>

      <div className="lb-stats">
        <div className="g">
          <div className="n">{data.length}</div>
          <div className="l">турниров</div>
        </div>
        <div className="g">
          <div className="n gold">{wins}</div>
          <div className="l">победы</div>
        </div>
        <div className="g">
          <div className="n green">{itmPct}%</div>
          <div className="l">в очках</div>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="lb-head">
          <span>Место · Турнир</span>
          <span>Очки</span>
        </div>
        {list.map((t) => {
          // Same row shape as the profile's «Последние 10 турниров»:
          // place badge — tournament — date, points on the right.
          const top = t.position !== null && t.position <= 3;
          return (
            <button
              className="recent-row"
              key={t.gameId}
              onClick={() => navigate(`/tournaments/${t.gameId}/results`)}
            >
              <div className={`pos-badge ${top ? 'pos-badge--top' : 'pos-badge--low'}`}>
                {t.position ?? '—'}
              </div>
              <div className="info">
                <div className="t">
                  {t.title ?? t.format ?? 'Турнир'}
                  {t.position === 1 ? ' ♛' : ''}
                </div>
                <div className="d">{shortDate(t.startTime)}</div>
              </div>
              {t.points > 0 ? (
                <div className="pts">+{t.points}</div>
              ) : (
                <div className="pts pts--none">без очков</div>
              )}
            </button>
          );
        })}
        {list.length === 0 && <div className="muted tiny">Нет турниров в этом фильтре</div>}
      </div>
    </div>
  );
}
